import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { GenericErrorResponse } from '../../../api';
import { ServiceError } from '../../../hooks/error';
import { TimerProps, useCountDown } from '../../../hooks/timer';
import { LoginData } from '../../../models';
import { LocationState } from '../../../models/Location';
import schemaLoginForm from '../schema/schemaLoginForm';
import { CUSTOMIZED_UNAUTHORIZED_STATUS_CODE } from '../../../api/config/misc';
import RouteKey from '../../../routes/key';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { usePostLogin } from '../../../api-refactored/user/usePostLogin';
import { useAppDispatch } from '../../../store';
import { updateUsername } from '../../../store/appSlice';

export interface UseLoginFormReturn {
  /**
   * Use timer hook object, use for the countdown
   */
  countDownObj: TimerProps;
  /**
   * React hook form object for logic form
   */
  form: UseFormReturn<LoginData>;
  /**
   * Function to trigger login api
   */
  login: (data: LoginData) => void;
  /**
   * Extra error handling for login api
   */
  loginError: string;
  /**
   * React-query useMutation return
   */
  loginMutation: UseCustomMutationReturn<any, AxiosError<GenericErrorResponse>, FormData, unknown>;
  /**
   * show captcha flag
   */
  showCaptcha: boolean;
  /**
   * Error message handling for count down related message
   */
  countDownError: string;
}

// @ts-ignore
const otpLoginFailedHandling = (location: Location<LocationState>) => {
  const {
    code,
    time,
    message: otpMessage,
  } = location?.state || {
    from: { pathname: '/', message: null, time: null },
  };

  return { code, time, otpMessage };
};

const removedOTPErrorStateWhenPageRefresh = (): void => {
  window.history.replaceState(null, document.title);
};

const { serverBusy } = RouteKey.error.child;

const { error: errorRoute } = RouteKey.child;

/**
 * A hook to handle logic logic
 * @returns
 */
export const useLoginForm = (): UseLoginFormReturn => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { code, time, otpMessage } = otpLoginFailedHandling(location);
  const countDownObj = useCountDown(time || 0);
  const [loginError, setLoginError] = useState('');
  const { t } = useTranslation();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [countDownError, setCountDownError] = useState('');

  const showHideLockedAccountError = useCallback((): void => {
    if (countDownError && countDownObj.countDownEnded) {
      setLoginError('');
      setShowCaptcha(true);
    }
  }, [countDownError, countDownObj.countDownEnded]);

  const renderRedirectError = useCallback(
    (errorCode?: string): void => {
      switch (errorCode) {
        case '1006':
          setLoginError(otpMessage);
          break;
        case '1008':
          setLoginError(t('profile->>LoginForm.AccountIsDisabled'));
          break;
        case '1009':
          setLoginError(t('profile->>LoginForm.UserAccountIsLocked'));
          break;
        case '4008':
            setLoginError(t('profile->>LoginForm.MIMSAccountNotExisted'));
            break;
        default:
      }
    },
    [t, otpMessage]
  );

  useEffect(() => {
    removedOTPErrorStateWhenPageRefresh();
  }, []);

  useEffect(() => {
    renderRedirectError(code);
  }, [code, renderRedirectError]);

  useEffect(() => {
    showHideLockedAccountError();
  }, [countDownObj.countDownEnded, showHideLockedAccountError]);

  useEffect(() => {
    // do nothing
  });
  const form = useForm<LoginData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: joiResolver(schemaLoginForm),
  });

  const loginMutation = usePostLogin({
    onSuccess: () => {
      history.replace('/auth/otp', location.state || { from: { pathname: '/', search: '?' } });
    },
    onError: (err) => loginFailCallback(err),
  });

  const INVALID_USERNAME_AND_PASSWORD = t('profile->>LoginForm.InvalidUsernameAndPassword');

  const loginFailCallback = (err: ServiceError) => {
    form.reset();
    switch (err.response?.status) {
      case CUSTOMIZED_UNAUTHORIZED_STATUS_CODE:
        if (err.response?.data?.code?.toString() === '1008' || err.response?.data?.code?.toString() === '1024') {
          // Account blocked
          showLoginError(err, err.response?.data.message);
        } else if (err.response?.data?.code?.toString() === '1020') {
          history.push(`/${errorRoute}/${serverBusy}`);
        } else {
          showLoginError(err);
        }
        break;
      case 0:
      default:
        showLoginError(err);
        break;
    }
  };

  const showLoginError = (err: ServiceError, errorMessage?: string) => {
    setLoginError('');
    setShowCaptcha(false);

    if (err.response?.data?.data?.waitingTime) {
      countDownObj.reset((err.response?.data?.data.waitingTime as unknown as number) * 1000);
    }

    let message = errorMessage;

    if (err.response?.data?.data?.isLocked) {
      message = t('profile->>LoginForm.User account is locked.');
    } else if (err.response?.data?.data?.maxAttempt) {
      message = t('profile->>LoginForm.AttemptsRemainingLocked', {
        maxAttempt: err.response?.data?.data?.maxAttempt,
      });
    }

    if (err.response?.data?.data?.waitingTime) {
      setCountDownError('profile->>LoginForm.PleaseTryAgain');
    } else if (err.response?.status === 403) {
      setLoginError(t('profile->>LoginForm.PleaseRefreshThePage'));
    } else if (!message) {
      setLoginError(INVALID_USERNAME_AND_PASSWORD);
    }

    if (message) {
      setLoginError(message);
    }
  };
  const dispatch = useAppDispatch();
  const login = (data: LoginData) => {
    const formData = new FormData();
    formData.append('username', data.username);
    formData.append('password', data.password);
    dispatch(updateUsername(data.username));
    loginMutation.mutate(formData);
  };

  return {
    login,
    loginMutation,
    form,
    loginError,
    countDownObj,
    showCaptcha,
    countDownError,
  };
};
